import { useState } from 'react';
import { CheckCircle, XCircle, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { QuizResult } from '../types';

interface QuizResultsProps {
    result: QuizResult;
    onRetake?: () => void;
    onContinue?: () => void;
}

export function QuizResults({ result, onRetake, onContinue }: QuizResultsProps) {
    const [showReview, setShowReview] = useState(false);

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 60) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getScoreBgColor = (score: number) => {
        if (score >= 80) return 'bg-green-50 border-green-200';
        if (score >= 60) return 'bg-yellow-50 border-yellow-200';
        return 'bg-red-50 border-red-200';
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow border border-slate-200">
            {/* Header */}
            <div className="text-center mb-6">
                <div className="mb-4">
                    {result.passed ? (
                        <CheckCircle size={64} className="text-green-500 mx-auto" />
                    ) : (
                        <XCircle size={64} className="text-red-500 mx-auto" />
                    )}
                </div>
                
                <h2 className="text-2xl font-semibold text-slate-800 mb-2">
                    {result.passed ? 'Quiz Passed!' : 'Quiz Failed'}
                </h2>
                
                <div className={`inline-flex items-center px-4 py-2 rounded-lg border ${getScoreBgColor(result.percentage)}`}>
                    <span className={`text-2xl font-bold ${getScoreColor(result.percentage)}`}>
                        {result.percentage}%
                    </span>
                    <span className="ml-2 text-slate-600">
                        ({result.correct_answers}/{result.total_questions} correct)
                    </span>
                </div>
            </div>

            {/* Score Details */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                        <div className="text-2xl font-bold text-slate-800">{result.correct_answers}</div>
                        <div className="text-sm text-slate-600">Correct Answers</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-slate-800">{result.incorrect_answers}</div>
                        <div className="text-sm text-slate-600">Incorrect Answers</div>
                    </div>
                </div>
            </div>

            {/* Review Section */}
            {result.question_results && result.question_results.length > 0 && (
                <div className="mb-6">
                    <button
                        onClick={() => setShowReview(!showReview)}
                        className="flex items-center text-sky-600 hover:text-sky-700 mb-4"
                    >
                        {showReview ? (
                            <>
                                <EyeOff size={16} className="mr-2" />
                                Hide Answer Review
                            </>
                        ) : (
                            <>
                                <Eye size={16} className="mr-2" />
                                Review Your Answers
                            </>
                        )}
                    </button>

                    {showReview && (
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {result.question_results.map((review, index) => (
                                <div
                                    key={review.question_id}
                                    className={`p-4 rounded-lg border ${
                                        review.is_correct
                                            ? 'bg-green-50 border-green-200'
                                            : 'bg-red-50 border-red-200'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-600">
                                            Question {index + 1}
                                        </span>
                                        {review.is_correct ? (
                                            <CheckCircle size={16} className="text-green-500" />
                                        ) : (
                                            <XCircle size={16} className="text-red-500" />
                                        )}
                                    </div>
                                    
                                    <h4 className="font-medium text-slate-800 mb-3">
                                        {review.question}
                                    </h4>
                                    
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-slate-600 w-20">Your Answer:</span>
                                            <span className={`text-sm ${
                                                review.is_correct ? 'text-green-700' : 'text-red-700'
                                            }`}>
                                                Option {review.selected_answer}
                                            </span>
                                        </div>
                                        
                                        {!review.is_correct && (
                                            <div className="flex items-center">
                                                <span className="text-sm font-medium text-slate-600 w-20">Correct Answer:</span>
                                                <span className="text-sm text-green-700">
                                                    Option {review.correct_answer}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
                {!result.passed && onRetake && (
                    <button
                        onClick={onRetake}
                        className="flex items-center px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                    >
                        <RotateCcw size={16} className="mr-2" />
                        Retake Quiz
                    </button>
                )}
                
                {onContinue && (
                    <button
                        onClick={onContinue}
                        className={`px-6 py-2 rounded-lg ${
                            result.passed
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-slate-600 text-white hover:bg-slate-700'
                        }`}
                    >
                        {result.passed ? 'Continue to Certificate' : 'Continue'}
                    </button>
                )}
            </div>

            {/* Pass/Fail Message */}
            <div className="mt-6 text-center">
                {result.passed ? (
                    <p className="text-green-600 font-medium">
                        Congratulations! You have successfully completed the quiz and can now receive your certificate.
                    </p>
                ) : (
                    <p className="text-red-600 font-medium">
                        You need to score at least 70% to pass. Please review the material and try again.
                    </p>
                )}
            </div>
        </div>
    );
}
